import { components, paths } from "@securityjourney/svc-learner-ai/dist/gen/hints";

import { OpenApiFetchWrapper, type ServiceResponse } from "../openApiFetchWrapper";

type HintsEnabledRequest =
  components["schemas"]["securityjourney.proto.learner_ai.CodeSubHintsEnabledStatusRequest"];
type HintsEnabledResponse =
  components["schemas"]["securityjourney.proto.learner_ai.CodeSubHintsEnabledStatusResponse"];
type RetrieveHintResponse =
  components["schemas"]["securityjourney.proto.learner_ai.CodeSubHintsResponse"];
type HintFeedbackRequest =
  components["schemas"]["securityjourney.proto.learner_ai.UserFeedbackRequest"];
type ExplainSubmissionRequest =
  components["schemas"]["securityjourney.proto.learner_ai.ExplainSubmissionRequest"];
export type ExplainSubmissionResponse =
  components["schemas"]["securityjourney.proto.learner_ai.ExplainSubmissionResponse"];

export class HintsClient extends OpenApiFetchWrapper<paths> {
  constructor() {
    super();
  }

  async checkIfHintsAreEnabled({
    tenant,
  }: HintsEnabledRequest): Promise<ServiceResponse<HintsEnabledResponse>> {
    try {
      const { data, error } = await this.get(`/svc/learner-ai/{tenant}/code-sub/hints/enabled`, {
        params: {
          path: { tenant },
        },
      });

      if (data) {
        return { data, error: null };
      }

      return {
        data: null,
        error: new Error((error as any)?.message || "Failed to check if hints are enabled"),
      };
    } catch (error: any) {
      return { data: null, error: new Error(error.message || "Fetch error") };
    }
  }

  public async retrieveAllHints({
    tenant,
    lessonKey,
  }: {
    tenant: string;
    lessonKey: string;
  }): Promise<ServiceResponse<RetrieveHintResponse>> {
    try {
      const { data, error } = await this.get(`/svc/learner-ai/{tenant}/code-sub/hints`, {
        params: {
          path: { tenant },
          query: { lessonKey },
        },
      });

      if (data) {
        return { data, error: null };
      }

      return {
        data: null,
        error: new Error((error as any)?.message || "Failed to retrieve hints"),
      };
    } catch (error: any) {
      return { data: null, error: new Error(error.message || "Fetch error") };
    }
  }

  async retrieveHint({
    tenant,
    submissionId,
  }: {
    tenant: string;
    submissionId: string;
  }): Promise<ServiceResponse<RetrieveHintResponse>> {
    try {
      const { data, error } = await this.get(`/svc/learner-ai/{tenant}/code-sub/hints`, {
        params: {
          path: { tenant },
          query: { submissionId },
        },
      });

      if (data) {
        return { data, error: null };
      }

      return {
        data: null,
        error: new Error((error as any)?.message || "Failed to retrieve hint"),
      };
    } catch (error: any) {
      return { data: null, error: new Error(error.message || "Fetch error") };
    }
  }

  async generateHint({
    tenant,
    submissionId,
    lessonKey,
  }: {
    tenant: string;
    submissionId: string;
    lessonKey: string;
  }) {
    try {
      const { data, error } = await this.post(`/svc/learner-ai/{tenant}/code-sub/hints`, {
        params: {
          path: { tenant },
        },
        body: {
          submissionId,
          lessonKey,
        },
      });

      if (data) {
        return { data, error: null };
      }

      return {
        data: null,
        error: new Error((error as any)?.message || "Failed to generate hint"),
      };
    } catch (error: any) {
      return { data: null, error: new Error(error.message || "Fetch error") };
    }
  }

  async provideHintFeedback({
    tenant,
    submissionId,
    feedback,
  }: HintFeedbackRequest): Promise<void> {
    const { error } = await this.post(`/svc/learner-ai/{tenant}/code-sub/hints/feedback`, {
      params: {
        path: { tenant },
      },
      body: {
        submissionId,
        feedback,
      },
    });

    if (error) {
      throw new Error((error as any)?.message || "Failed to provide hint feedback");
    }
  }

  async getSubmissionExplanation(
    request: ExplainSubmissionRequest
  ): Promise<ServiceResponse<ExplainSubmissionResponse>> {
    try {
      const encodedSubmission = btoa(request.learnerSubmission);
      const { data, error } = await this.post("/svc/learner-ai/patch", {
        body: {
          lessonKey: request.lessonKey,
          language: request.language,
          learnerSubmission: encodedSubmission,
        },
      });

      if (data) {
        return { data, error: null };
      }

      return {
        data: null,
        error: new Error((error as any)?.message || "Failed to retrieve patch files"),
      };
    } catch (error: any) {
      return { data: null, error: new Error(error.message || "Fetch error") };
    }
  }
}

let hintsClientInstance: HintsClient | null = null;

export const getHintsClient = (): HintsClient => {
  if (!hintsClientInstance) {
    hintsClientInstance = new HintsClient();
  }
  return hintsClientInstance;
};
